import { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Button,
  ButtonGroup,
  Stack,
  InlineStack,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';

export type ConfirmLeaveModalData = {
  /**
   * Custom message to show instead of the default unsaved-changes copy
   */
  message?: string;

  /**
   * Callback to save changes before navigating away.
   * If provided, a "Save & Continue" option will be shown.
   */
  onSave?: () => Promise<void> | void;

  /**
   * Callback to handle errors from the save operation
   */
  onError?: (error: unknown) => void;

  /**
   * Callback called when user chooses to discard changes
   */
  onDiscard?: () => void;
};

/**
 * Managed confirm-leave modal shown by `NavigationGuardProvider` when a
 * navigation is attempted with unsaved changes.
 *
 * Outcomes (observed by the provider via `modals.waitForClose`):
 * - `onConfirm()` — proceed with the blocked navigation ("Discard changes",
 *   or "Save & Continue" after a successful save)
 * - `onClose()` — cancel the navigation ("Go back", Esc, overlay click, or a
 *   failed save)
 */
export function ConfirmLeaveModal({
  modalState,
  data,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<ConfirmLeaveModalData>) {
  const [isSaving, setIsSaving] = useState(false);
  const { message, onSave, onError, onDiscard } = data;

  // Normalizes args: the Modal calls `onClose(event, reason)`, but the
  // manager's close callback treats its first argument as close data.
  const handleCancel = () => {
    onClose();
  };

  const handleDiscardAndContinue = () => {
    onDiscard?.();
    onConfirm();
  };

  const handleSaveAndContinue = async () => {
    if (onSave == null) {
      return;
    }

    try {
      setIsSaving(true);
      await onSave();
      onConfirm();
    } catch (error) {
      // Close the modal on save failure so the underlying page —
      // which typically owns the error UI (inline banner, form-field
      // errors) — becomes visible again. Without this, the modal
      // sits over the page hiding whatever feedback `onSave` already
      // rendered, and the user is left with only a stopped spinner.
      onClose();
      onError?.(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      state={modalState}
      onClose={handleCancel}
      title="Just a minute!"
      // Veto dismissals (Esc, overlay click, close button) while a save is
      // in flight — closing mid-save would let the pending `onSave` promise
      // resolve against a dismissed modal and leave the blocked navigation
      // in an ambiguous state. Explicit props win over the manager's `state`.
      isDismissable={!isSaving}
      // `isDismissable={false}` hides the close button by default; keep it
      // visible (rendered disabled) so the header doesn't jump mid-save.
      shouldShowCloseButton
    >
      <ModalBody>
        {message != null && !isEmptyString(message) ? (
          <Paragraph>{message}</Paragraph>
        ) : (
          <Stack gap="200">
            <Paragraph>You have unsaved changes.</Paragraph>
            <Paragraph>Please save your changes or discard them to continue.</Paragraph>
          </Stack>
        )}
      </ModalBody>
      <ModalFooter as={InlineStack} alignBlock="center" alignInline="space-between" gap="200">
        <Button variant="secondary" size="sm" onClick={handleCancel} disabled={isSaving}>
          Go back
        </Button>
        <ButtonGroup groupVariant="spaced">
          <Button variant="secondary" size="sm" isDanger onClick={handleDiscardAndContinue} disabled={isSaving}>
            Discard changes
          </Button>
          {onSave != null ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveAndContinue}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save & Continue
            </Button>
          ) : null}
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
}
